/* Ounce — the hand-drawn grout (theme/TILES.md).

   Every .tl (tile) gets lines drawn round it, in the gap between it and its neighbours, by the nearest "surface" it
   sits in: the page's .wall, or any element with data-tiles-self (a floating card, the basket drawer, the cookie bar),
   which also gets a line round its own edge. The lines are one SVG per surface (svg.ink, its last child): filled pen
   strokes whose centre wanders and whose width swells, the same stroke for the same place on every draw.

   They are redrawn, at most once a frame, when a surface changes size, when the window resizes, when the font has
   loaded, when an image loads, when anything inside a surface is added, removed, shown or hidden (the basket redrawn
   after a change, a section re-rendered in the theme editor), and when a box inside a surface scrolls. Other code
   can ask for a redraw with window.OunceTiles.redraw().

   On arrival the wall's lines are inked top to bottom and its rows settle in, once per page load (the CSS in
   ounce.css, .wall.in and svg.ink.in); not at all for people who ask for less motion. */
(() => {
  'use strict';
  const SURFACE = '.wall, [data-tiles-self]';
  const NS = 'http://www.w3.org/2000/svg';
  const phoneQ = matchMedia('(max-width: 720px)');

  // ---- one pen stroke ----
  function rng(seed) { return () => { seed |= 0; seed = seed + 0x6D2B79F5 | 0; let t = Math.imul(seed ^ seed >>> 15, 1 | seed); t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t; return ((t ^ t >>> 14) >>> 0) / 4294967296; }; }
  function smooth(arr, k) { const i = Math.min(arr.length - 2, Math.floor(k)), f = k - i, s = (1 - Math.cos(f * Math.PI)) / 2; return arr[i] * (1 - s) + arr[i + 1] * s; }
  // A straight segment drawn as a filled outline: the centre wanders a little, the width swells, the ends taper.
  // Seeded by where the segment is, so a redraw draws the same line.
  function stroke(x1, y1, x2, y2, phone) {
    const L = Math.hypot(x2 - x1, y2 - y1); if (!(L >= 1)) return '';
    const r = rng(Math.round(x1 / 6) * 73856093 ^ Math.round(y1 / 6) * 19349663 ^ Math.round(L / 6) * 83492791);
    const ux = (x2 - x1) / L, uy = (y2 - y1) / L, nx = -uy, ny = ux;
    const amp = phone ? 0.95 : 1.3, wb = phone ? 1.9 : 2.4;
    const n = Math.max(2, Math.ceil(L / 70) + 1);
    const off = [], wid = [];
    const tilt = (r() * 2 - 1) * amp * 0.8;
    for (let i = 0; i < n; i++) { off.push((r() * 2 - 1) * amp + tilt * (i / (n - 1) - .5) * 2); wid.push(wb * (0.75 + r() * 0.5)); }
    const s0 = -(r() * 1.6 - 0.4), s1 = L + (r() * 1.6 - 0.4);
    const up = [], dn = [];
    for (let s = s0; ; s = Math.min(s + 6, s1)) {
      const k = Math.max(0, Math.min(1, (s - s0) / (s1 - s0))) * (n - 1);
      const o = smooth(off, k), endTaper = Math.min(1, 0.55 + Math.min(s - s0, s1 - s) / 6);
      const w = smooth(wid, k) * endTaper / 2;
      const px = x1 + ux * s, py = y1 + uy * s;
      up.push((px + nx * (o - w)).toFixed(1) + ' ' + (py + ny * (o - w)).toFixed(1));
      dn.push((px + nx * (o + w)).toFixed(1) + ' ' + (py + ny * (o + w)).toFixed(1));
      if (s >= s1) break;
    }
    return 'M' + up.join('L') + 'L' + dn.reverse().join('L') + 'Z';
  }
  // Edges on the same line (within 1.2px) are collected together, then overlapping pieces merged into one stroke,
  // so two tiles that share an edge share one line.
  function addSeg(map, c, a, b) {
    for (const k of map.keys()) if (Math.abs(k - c) < 1.2) { map.get(k).push([a, b]); return; }
    map.set(c, [[a, b]]);
  }
  function merged(list) {
    list.sort((p, q) => p[0] - q[0]); const out = [];
    for (const s of list) { const last = out[out.length - 1]; if (last && s[0] <= last[1] + 1.5) last[1] = Math.max(last[1], s[1]); else out.push([...s]); }
    return out;
  }

  // A tile's box as far as it can be seen inside its surface: cut by any box between them that clips what overflows it
  // (a scrolling list), so a tile scrolled out of view draws nothing and one half in view draws only its visible part.
  function visible(t, el, clipCache) {
    let r = t.getBoundingClientRect();
    let x1 = r.left, y1 = r.top, x2 = r.right, y2 = r.bottom;
    for (let p = t.parentElement; p && p !== el; p = p.parentElement) {
      let clip = clipCache.get(p);
      if (clip === undefined) {
        const cs = getComputedStyle(p);
        clip = (cs.overflowX !== 'visible' || cs.overflowY !== 'visible') ? p.getBoundingClientRect() : null;
        clipCache.set(p, clip);
      }
      if (clip) { x1 = Math.max(x1, clip.left); y1 = Math.max(y1, clip.top); x2 = Math.min(x2, clip.right); y2 = Math.min(y2, clip.bottom); }
    }
    return { x1, y1, x2, y2 };
  }

  const firstDraw = new WeakSet();
  // Lines round every .tl that belongs to el (the nearest surface it sits in is el), and round el itself when self.
  function draw(el, self) {
    if (!el || !el.isConnected) return;
    let svg = el.querySelector(':scope > svg.ink');
    const shown = el.getClientRects().length > 0;
    if (!shown) { if (svg) svg.firstChild.setAttribute('d', ''); return; }
    if (!svg) {
      svg = document.createElementNS(NS, 'svg'); svg.setAttribute('class', 'ink'); svg.setAttribute('aria-hidden', 'true'); svg.setAttribute('focusable', 'false');
      svg.appendChild(document.createElementNS(NS, 'path')); el.appendChild(svg);
      if (getComputedStyle(el).position === 'static') el.style.position = 'relative';
    }
    const W = el.getBoundingClientRect(), g = parseFloat(getComputedStyle(el).getPropertyValue('--g')) || 5;
    const phone = phoneQ.matches, H = new Map(), V = new Map(), clipCache = new Map();
    const boxes = [];
    for (const t of el.querySelectorAll('.tl')) {
      if (t.parentElement.closest(SURFACE) !== el) continue;   // it sits in another surface, which draws it
      boxes.push(visible(t, el, clipCache));
    }
    // its own edge, drawn half a gap inside it: where the edges of tiles set one gap in from it are drawn, so they share it
    if (self) boxes.push({ x1: W.left + g, y1: W.top + g, x2: W.right - g, y2: W.bottom - g });
    for (const b of boxes) {
      if (b.x2 - b.x1 < 2 || b.y2 - b.y1 < 2) continue;
      const x1 = b.x1 - W.left - g / 2, x2 = b.x2 - W.left + g / 2, y1 = b.y1 - W.top - g / 2, y2 = b.y2 - W.top + g / 2;
      addSeg(H, y1, x1, x2); addSeg(H, y2, x1, x2); addSeg(V, x1, y1, y2); addSeg(V, x2, y1, y2);
    }
    let d = '';
    for (const [y, l] of H) for (const [a, b] of merged(l)) d += stroke(a, y, b, y, phone);
    for (const [x, l] of V) for (const [a, b] of merged(l)) d += stroke(x, a, x, b, phone);
    svg.setAttribute('width', W.width); svg.setAttribute('height', W.height);
    svg.setAttribute('viewBox', `0 0 ${W.width} ${W.height}`);
    svg.firstChild.setAttribute('d', d);

    // the arrival, once per page load: the wall's rows settle in one after another and the lines ink in
    if (!self && !firstDraw.has(el)) {
      firstDraw.add(el);
      el.querySelectorAll('.row').forEach((r, i) => r.style.setProperty('--i', Math.min(i, 8)));
      el.classList.add('in'); svg.classList.add('in');
    }
  }

  // ---- when to draw ----
  const watched = new WeakSet();
  const ro = new ResizeObserver(() => redraw());
  function watch() {
    for (const el of document.querySelectorAll(SURFACE)) if (!watched.has(el)) { watched.add(el); ro.observe(el); }
  }
  let queued = false;
  function redraw() {
    if (queued) return; queued = true;
    requestAnimationFrame(() => {
      queued = false; watch();
      for (const el of document.querySelectorAll(SURFACE)) draw(el, el.hasAttribute('data-tiles-self'));
    });
  }

  // anything added, removed, shown or hidden inside a surface, or a new surface anywhere; not the lines' own changes
  const touches = n => n.nodeType === 1 && (n.matches(SURFACE) || n.querySelector(SURFACE) || n.closest(SURFACE));
  new MutationObserver(list => {
    for (const m of list) {
      const n = m.target;
      if (n.nodeType !== 1 || n.closest('svg.ink')) continue;
      if (m.type === 'attributes' ? n.closest(SURFACE) : (n.closest(SURFACE) || [...m.addedNodes, ...m.removedNodes].some(touches))) { redraw(); return; }
    }
  }).observe(document.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden', 'open', 'class'] });

  addEventListener('resize', redraw, { passive: true });
  phoneQ.addEventListener('change', redraw);
  addEventListener('load', redraw);
  document.addEventListener('load', e => { if (e.target.tagName === 'IMG' && e.target.closest(SURFACE)) redraw(); }, true);
  document.addEventListener('scroll', e => { const t = e.target; if (t !== document && t.nodeType === 1 && t.closest(SURFACE)) redraw(); }, { capture: true, passive: true });
  if (document.fonts) { document.fonts.ready.then(redraw); document.fonts.addEventListener?.('loadingdone', redraw); }
  document.addEventListener('shopify:section:load', redraw);

  window.OunceTiles = { redraw, draw };
  redraw();
})();
